'use client';

import { useEffect } from 'react';

/**
 * Top-level fatal error boundary. Renders when the root layout itself throws,
 * so it must include its own <html> and <body> and cannot rely on any provider
 * mounted higher up (no MUI ThemeProvider available here). Keep it minimal
 * and dependency-free.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('[app/global-error] fatal error:', error);
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          fontFamily:
            'var(--font-inter), Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          background: '#f8fafc',
          color: '#0f172a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div
          style={{
            maxWidth: 520,
            width: '100%',
            background: '#ffffff',
            border: '1px solid',
            borderRadius: '6px',
            padding: 40,
            textAlign: 'center',
            boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '6px',
              background: 'rgba(239,68,68,0.10)',
              color: '#b91c1c',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 32,
              marginBottom: 16,
            }}
            aria-hidden
          >
            !
          </div>
          <h1
            style={{
              fontSize: 24,
              fontWeight: 800,
              letterSpacing: '-0.02em',
              margin: '0 0 8px',
            }}
          >
            Application error
          </h1>
          <p style={{ fontSize: 14, color: '#475569', margin: '0 0 24px' }}>
            {error?.message ||
              'A fatal error prevented the app from rendering. Please try again or reload the page.'}
          </p>
          {error?.digest && (
            <p
              style={{
                fontSize: 11,
                color: '#94a3b8',
                fontFamily: 'var(--font-mono), monospace',
                margin: '0 0 16px',
              }}
            >
              ref: {error.digest}
            </p>
          )}
          <div
            style={{
              display: 'flex',
              gap: 10,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={() => reset()}
              style={{
                background: '#0f172a',
                color: '#ffffff',
                border: 'none',
                padding: '10px 18px',
                borderRadius: '6px',
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => {
                if (typeof window !== 'undefined') window.location.reload();
              }}
              style={{
                background: '#ffffff',
                color: '#0f172a',
                border: '1px solid',
                padding: '10px 18px',
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Reload page
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
